import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { supabase } from "@/lib/supabase";
import { jobColumnFor } from "@/lib/jobIdentifier";
import type { ChangeOrder, ChangeOrderLineItem } from "@/data/changeOrderTypes";
import type { InstallFollowUpTask } from "@/data/unifiedFollowUp";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";
import type { Contractor } from "@/data/types";
import { AuditLogService } from "@/services/audit.service";

export interface JobLookup {
  id: string;
  job_number: string | null;
}

export interface JobAuditEntry {
  id: string;
  action: "create" | "update";
  userId: string | null;
  editedBy: string | null;
  changedFields: Record<string, unknown> | null;
  createdAt: string;
}

export interface MaterialsAuditDiff {
  added: { sku: string; name: string; qty: number }[];
  removed: { sku: string; name: string }[];
  changed: { sku: string; name: string; oldQty: number; newQty: number }[];
}

type RawJobAuditRow = {
  id: string;
  action: string;
  user_id: string | null;
  full_name?: string | null;
  changed_fields: Record<string, unknown> | null;
  created_at: string;
};

const DEFAULT_JOB_AUDIT_LOG_LIMIT = 100;

async function queryJobAuditLog(jobId: string, includeFullName: boolean, limit: number) {
  const selectColumns = includeFullName
    ? "id,action,user_id,full_name,changed_fields,created_at"
    : "id,action,user_id,changed_fields,created_at";

  return supabase
    .from("job_audit_log")
    .select(selectColumns)
    .eq("job_id", jobId)
    .order("created_at", { ascending: false })
    .limit(limit);
}

export async function getJobAuditLog(jobId: string, limit = DEFAULT_JOB_AUDIT_LOG_LIMIT): Promise<JobAuditEntry[]> {
  let { data, error } = await queryJobAuditLog(jobId, true, limit);

  if (error && error.code === "42703") {
    console.warn("[getJobAuditLog] full_name column unavailable; falling back to profile lookup");
    const fallback = await queryJobAuditLog(jobId, false, limit);
    data = fallback.data;
    error = fallback.error;
  }

  if (error || !data) {
    console.warn("[getJobAuditLog] error:", error);
    return [];
  }

  // Only look up profiles for rows where the trigger didn't capture full_name
  // (older rows written before the full_name column was added).
  const rows = (data ?? []) as unknown as RawJobAuditRow[];
  const rowsMissingName = rows.filter(
    (r) => !r.full_name && r.user_id,
  );

  const nameMap: Record<string, string> = {};
  if (rowsMissingName.length > 0) {
    const userIds = [...new Set(rowsMissingName.map((r) => r.user_id as string))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, auth_user_id, full_name")
      .or(userIds.map((id) => `id.eq.${id},auth_user_id.eq.${id}`).join(","));
    for (const p of profiles ?? []) {
      const profile = p as { id?: string | null; auth_user_id?: string | null; full_name?: string | null };
      const name = profile.full_name ?? null;
      if (name) {
        if (profile.id) nameMap[profile.id] = name;
        if (profile.auth_user_id) nameMap[profile.auth_user_id] = name;
      }
    }
  }

  return rows.map((row) => {
    const editedBy =
      row.full_name ??
      (row.user_id ? (nameMap[row.user_id] ?? null) : null);

    return {
      id: row.id,
      action: row.action as "create" | "update",
      userId: row.user_id,
      editedBy,
      changedFields: row.changed_fields ?? null,
      createdAt: row.created_at,
    };
  });
}

/**
 * Write a materials-change audit entry into job_audit_log.
 * Requires the job_audit_log_app_material_insert RLS policy to be applied.
 */
export async function writeJobMaterialsAuditEntry(
  jobId: string,
  organizationId: string,
  diff: MaterialsAuditDiff,
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data: profile } = await supabase
    .from("profiles")
    .select("full_name")
    .eq("auth_user_id", user.id)
    .single();

  const { error } = await supabase
    .from("job_audit_log")
    .insert({
      job_id: jobId,
      organization_id: organizationId,
      user_id: user.id,
      action: "update",
      changed_fields: { materials_list: diff },
      snapshot: null,
      full_name: profile?.full_name ?? null,
    });
  if (error) {
    console.warn("[writeJobMaterialsAuditEntry] failed to write audit entry:", error);
  }
}

/**
 * Write a re-allocation audit entry into job_audit_log.
 * direction "out" = this job gave materials away; "in" = this job received materials.
 */
export async function writeReallocationAuditEntry(
  jobId: string,
  organizationId: string,
  direction: "in" | "out",
  otherJobLabel: string,
  sku: string,
  productName: string,
  qty: number,
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data: profile } = await supabase
    .from("profiles")
    .select("full_name")
    .eq("auth_user_id", user.id)
    .single();

  const { error } = await supabase
    .from("job_audit_log")
    .insert({
      job_id: jobId,
      organization_id: organizationId,
      user_id: user.id,
      action: "update",
      changed_fields: {
        reallocation: { direction, otherJobLabel, sku, productName, qty },
      },
      snapshot: null,
      full_name: profile?.full_name ?? null,
    });
  if (error) {
    console.warn("[writeReallocationAuditEntry] failed to write audit entry:", error);
  }
}

export interface SchedulingEventRow {
  id: string;
  organization_id: string;
  job_id: string;
  contractor_id: string | null;
  event_type: string;
  start_at: string;
  end_at: string | null;
  status: string;
  notes: string | null;
  created_by: string | null;
  brief_snapshot?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface DispatchAssignmentRow {
  id: string;
  organization_id: string;
  job_id: string;
  contractor_id: string;
  assigned_by: string | null;
  status: string;
  assigned_at: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface ChangeOrderRow {
  id: string;
  organization_id: string;
  job_id: string;
  quote_id: string | null;
  type: string;
  description: string | null;
  amount_delta: number;
  status: string;
  requested_by: string | null;
  approved_by: string | null;
  created_at: string;
  updated_at: string;
}

interface ChangeOrderItemRow {
  id: string;
  change_order_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  created_at: string;
}

interface InstallerWrapRecordRow {
  id: string;
  organization_id: string;
  job_id: string;
  installer_id: string | null;
  wrap_date: string;
  status: string;
  outcome: string | null;
  checklist: unknown;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface InstallFollowUpTaskRow {
  id: string;
  organization_id: string;
  job_id: string;
  wrap_record_id: string | null;
  task_type: string;
  status: string;
  due_date: string | null;
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  return user?.id ?? null;
}

async function resolveJobUuid(jobNumber: string): Promise<string | null> {
  const { data } = await supabase
    .from("jobs")
    .select("id")
    .eq(jobColumnFor(jobNumber), jobNumber)
    .single();

  return data?.id ?? null;
}

function normalizeUuid(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(trimmed)
    ? trimmed
    : null;
}

function parseNotesJson(notes: string | null): Record<string, unknown> {
  if (!notes) return {};

  try {
    const parsed = JSON.parse(notes);
    return parsed && typeof parsed === "object" ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function toChangeOrderLineItems(items: ChangeOrderItemRow[]): ChangeOrderLineItem[] {
  return items.map((item) => ({
    description: item.description,
    amount: Number(item.line_total ?? item.unit_price ?? 0),
    quantity: item.quantity ?? undefined,
  }));
}

function toUiChangeOrder(
  row: ChangeOrderRow,
  items: ChangeOrderItemRow[],
  jobNumber: string,
  orderNumber: number,
): ChangeOrder {
  const lineItems = toChangeOrderLineItems(items);
  const changeAmount = Number(row.amount_delta ?? 0);
  const currentContractValue = 0;

  return {
    id: row.id,
    jobId: jobNumber,
    orderNumber,
    status: (row.status as ChangeOrder["status"]) ?? "Draft",
    summary: row.description ?? "",
    lineItems,
    originalContractValue: currentContractValue,
    changeAmount,
    revisedContractValue: currentContractValue + changeAmount,
    createdAt: row.created_at,
    createdBy: row.requested_by ?? "Current User",
    signedAt: row.status === "Signed" ? row.updated_at : undefined,
    sentToCustomerAt: row.updated_at,
    notes: row.type,
  };
}

function toUiWrapRecord(row: InstallerWrapRecordRow, jobNumber: string): InstallerWrapRecord {
  const checklist = row.checklist && typeof row.checklist === "object"
    ? row.checklist as Record<string, unknown>
    : {};

  const customerComm = checklist.customerComm && typeof checklist.customerComm === "object"
    ? checklist.customerComm as InstallerWrapRecord["customerComm"]
    : {
        customerInformed: false,
        rescheduleNeeded: false,
        communicationSummary: "",
      };

  return {
    id: row.id,
    jobId: jobNumber,
    installerId: row.installer_id ?? "installer",
    timestamp: typeof checklist.timestamp === "string" ? checklist.timestamp : row.created_at,
    outcome: (row.outcome as InstallerWrapRecord["outcome"]) ?? "partial_reschedule",
    progressNotes: typeof checklist.progressNotes === "string" ? checklist.progressNotes : row.notes ?? "",
    anotherDayNeeded: Boolean(checklist.anotherDayNeeded),
    customerComm,
    settlementStatus: (checklist.settlementStatus as InstallerWrapRecord["settlementStatus"]) ?? "continuation_required",
    schedulingFollowUpRequired: Boolean(checklist.schedulingFollowUpRequired),
    closeoutEligible: Boolean(checklist.closeoutEligible),
    completionPercent: Number(checklist.completionPercent ?? 0),
    paymentCapture: checklist.paymentCapture as InstallerWrapRecord["paymentCapture"],
    serviceChecklist: checklist.serviceChecklist as InstallerWrapRecord["serviceChecklist"],
  };
}

function toUiInstallFollowUpTask(row: InstallFollowUpTaskRow, jobNumber: string): InstallFollowUpTask {
  const extras = parseNotesJson(row.notes);
  return {
    id: row.id,
    jobId: jobNumber,
    wrapRecordId: row.wrap_record_id ?? "",
    type: "install_follow_up",
    reason: (row.task_type as InstallFollowUpTask["reason"]) ?? "partial_reschedule",
    reasonLabel: typeof extras.reasonLabel === "string" ? extras.reasonLabel : row.task_type,
    communicationSummary: typeof extras.communicationSummary === "string" ? extras.communicationSummary : (row.notes ?? ""),
    proposedNextDate: typeof extras.proposedNextDate === "string" ? extras.proposedNextDate : undefined,
    completionPercent: Number(extras.completionPercent ?? 0),
    assignedTo: "dispatch",
    status: (row.status as InstallFollowUpTask["status"]) ?? "open",
    createdAt: row.created_at,
    resolvedAt: row.status === "completed" ? row.updated_at : undefined,
    resolvedBy: row.assigned_to ?? undefined,
  };
}

export const JobOperationsService = {
  async getJobLookup(): Promise<JobLookup[]> {
    const { data, error } = await supabase
      .from("jobs")
      .select("id, job_number");

    if (error || !data) {
      console.warn("[JobOperationsService.getJobLookup] error:", error);
      return [];
    }

    return data as JobLookup[];
  },

  async listSchedulingEvents(): Promise<SchedulingEventRow[]> {
    const { data, error } = await supabase
      .from("scheduling_events")
      .select("*")
      .order("start_at", { ascending: true });

    if (error || !data) {
      console.warn("[JobOperationsService.listSchedulingEvents] error:", error);
      return [];
    }

    return data as SchedulingEventRow[];
  },

  async createSchedulingEvent(input: {
    jobNumber: string;
    contractorId?: string | null;
    eventType: string;
    startAt: string;
    endAt?: string | null;
    status: string;
    notes?: string;
    briefSnapshot?: Record<string, unknown> | null;
  }): Promise<SchedulingEventRow | null> {
    const [jobUuid, organizationId, userId] = await Promise.all([
      resolveJobUuid(input.jobNumber),
      getCurrentOrganizationId().catch(() => null),
      getCurrentUserId(),
    ]);
    const contractorId = normalizeUuid(input.contractorId);

    if (!jobUuid || !organizationId) return null;

    const { data, error } = await supabase
      .from("scheduling_events")
      .insert({
        organization_id: organizationId,
        job_id: jobUuid,
        contractor_id: contractorId,
        event_type: input.eventType,
        start_at: input.startAt,
        end_at: input.endAt ?? null,
        status: input.status,
        notes: input.notes ?? null,
        created_by: userId,
        brief_snapshot: input.briefSnapshot ?? null,
      })
      .select()
      .single();

    if (error || !data) {
      console.warn("[JobOperationsService.createSchedulingEvent] error:", error);
      return null;
    }

    AuditLogService.create({
      actionType: "scheduling_event_created",
      entityType: "scheduling_event",
      entityId: (data as SchedulingEventRow).id,
      outcome: "success",
      metadata: { eventType: input.eventType, startAt: input.startAt, endAt: input.endAt, contractorId: input.contractorId },
    }).catch(() => {});

    return data as SchedulingEventRow;
  },

  async updateSchedulingEvent(
    id: string,
    input: {
      jobNumber?: string;
      contractorId?: string | null;
      eventType?: string;
      startAt?: string;
      endAt?: string | null;
      status?: string;
      notes?: string;
      briefSnapshot?: Record<string, unknown> | null;
    },
  ): Promise<SchedulingEventRow | null> {
    const patch: Record<string, unknown> = {};

    if (input.jobNumber !== undefined) {
      const jobUuid = await resolveJobUuid(input.jobNumber);
      if (jobUuid) patch.job_id = jobUuid;
    }
    if (input.contractorId !== undefined) patch.contractor_id = normalizeUuid(input.contractorId);
    if (input.eventType !== undefined) patch.event_type = input.eventType;
    if (input.startAt !== undefined) patch.start_at = input.startAt;
    if (input.endAt !== undefined) patch.end_at = input.endAt;
    if (input.status !== undefined) patch.status = input.status;
    if (input.notes !== undefined) patch.notes = input.notes;
    if (input.briefSnapshot !== undefined) patch.brief_snapshot = input.briefSnapshot;

    const { data, error } = await supabase
      .from("scheduling_events")
      .update(patch)
      .eq("id", id)
      .select()
      .single();

    if (error || !data) {
      console.warn("[JobOperationsService.updateSchedulingEvent] error:", error);
      return null;
    }

    const oldEvent = patch;
    AuditLogService.create({
      actionType: "scheduling_event_updated",
      entityType: "scheduling_event",
      entityId: id,
      outcome: "success",
      previousValue: oldEvent,
      newValue: patch,
    }).catch(() => {});

    return data as SchedulingEventRow;
  },

  async deleteSchedulingEvent(id: string): Promise<boolean> {
    const { error } = await supabase
      .from("scheduling_events")
      .delete()
      .eq("id", id);

    if (error) {
      console.warn("[JobOperationsService.deleteSchedulingEvent] error:", error);
      return false;
    }

    AuditLogService.create({
      actionType: "scheduling_event_deleted",
      entityType: "scheduling_event",
      entityId: id,
      outcome: "success",
    }).catch(() => {});

    return true;
  },

  async listDispatchAssignments(): Promise<DispatchAssignmentRow[]> {
    const { data, error } = await supabase
      .from("dispatch_assignments")
      .select("*")
      .order("assigned_at", { ascending: false });

    if (error || !data) {
      console.warn("[JobOperationsService.listDispatchAssignments] error:", error);
      return [];
    }

    return data as DispatchAssignmentRow[];
  },

  async upsertDispatchAssignment(input: {
    jobNumber: string;
    contractorId: string;
    status: string;
    notes?: string;
  }): Promise<DispatchAssignmentRow | null> {
    const [jobUuid, organizationId, userId] = await Promise.all([
      resolveJobUuid(input.jobNumber),
      getCurrentOrganizationId().catch(() => null),
      getCurrentUserId(),
    ]);
    const contractorId = normalizeUuid(input.contractorId);

    if (!jobUuid || !organizationId || !contractorId) return null;

    const { data: existing } = await supabase
      .from("dispatch_assignments")
      .select("id")
      .eq("job_id", jobUuid)
      .maybeSingle();

    if (existing?.id) {
      const { data, error } = await supabase
        .from("dispatch_assignments")
        .update({
          contractor_id: contractorId,
          assigned_by: userId,
          status: input.status,
          notes: input.notes ?? null,
        })
        .eq("id", existing.id)
        .select()
        .single();

      if (error || !data) {
        console.warn("[JobOperationsService.upsertDispatchAssignment:update] error:", error);
        return null;
      }

      // Keep jobs mirror column in sync with dispatch assignment owner.
      const { error: mirrorError } = await supabase
        .from("jobs")
        .update({ contractor_id: contractorId })
        .eq("id", jobUuid);
      if (mirrorError) {
        console.warn("[JobOperationsService.upsertDispatchAssignment:update] jobs.contractor_id mirror error:", mirrorError);
      }

      return data as DispatchAssignmentRow;
    }

    const { data, error } = await supabase
      .from("dispatch_assignments")
      .insert({
        organization_id: organizationId,
        job_id: jobUuid,
        contractor_id: contractorId,
        assigned_by: userId,
        status: input.status,
        notes: input.notes ?? null,
      })
      .select()
      .single();

    if (error || !data) {
      console.warn("[JobOperationsService.upsertDispatchAssignment:insert] error:", error);
      return null;
    }

    // Keep jobs mirror column in sync with dispatch assignment owner.
    const { error: mirrorError } = await supabase
      .from("jobs")
      .update({ contractor_id: contractorId })
      .eq("id", jobUuid);
    if (mirrorError) {
      console.warn("[JobOperationsService.upsertDispatchAssignment:insert] jobs.contractor_id mirror error:", mirrorError);
    }

    return data as DispatchAssignmentRow;
  },

  async clearDispatchAssignment(jobNumber: string): Promise<boolean> {
    const jobUuid = await resolveJobUuid(jobNumber);
    if (!jobUuid) return false;

    const { error } = await supabase
      .from("dispatch_assignments")
      .delete()
      .eq("job_id", jobUuid);

    if (error) {
      console.warn("[JobOperationsService.clearDispatchAssignment] error:", error);
      return false;
    }

    // Clearing dispatch assignment also clears jobs mirror contractor link.
    const { error: mirrorError } = await supabase
      .from("jobs")
      .update({ contractor_id: null })
      .eq("id", jobUuid);
    if (mirrorError) {
      console.warn("[JobOperationsService.clearDispatchAssignment] jobs.contractor_id mirror error:", mirrorError);
    }

    return true;
  },

  async listChangeOrdersByJob(jobNumber: string): Promise<ChangeOrder[]> {
    const jobUuid = await resolveJobUuid(jobNumber);
    if (!jobUuid) return [];

    const { data: orders, error } = await supabase
      .from("change_orders")
      .select("*")
      .eq("job_id", jobUuid)
      .order("created_at", { ascending: true });

    if (error || !orders) {
      console.warn("[JobOperationsService.listChangeOrdersByJob] error:", error);
      return [];
    }

    const orderIds = (orders as ChangeOrderRow[]).map((row) => row.id);
    const { data: items } = orderIds.length > 0
      ? await supabase
          .from("change_order_items")
          .select("*")
          .in("change_order_id", orderIds)
          .order("created_at", { ascending: true })
      : { data: [] };

    const itemsByOrder = new Map<string, ChangeOrderItemRow[]>();
    ((items ?? []) as ChangeOrderItemRow[]).forEach((item) => {
      const list = itemsByOrder.get(item.change_order_id) ?? [];
      list.push(item);
      itemsByOrder.set(item.change_order_id, list);
    });

    return (orders as ChangeOrderRow[]).map((row, index) =>
      toUiChangeOrder(row, itemsByOrder.get(row.id) ?? [], jobNumber, index + 1),
    );
  },

  async createChangeOrder(jobNumber: string, changeOrder: ChangeOrder): Promise<ChangeOrder | null> {
    const [jobUuid, organizationId, userId] = await Promise.all([
      resolveJobUuid(jobNumber),
      getCurrentOrganizationId().catch(() => null),
      getCurrentUserId(),
    ]);

    if (!jobUuid || !organizationId) return null;

    const { data: orderRow, error: orderError } = await supabase
      .from("change_orders")
      .insert({
        organization_id: organizationId,
        job_id: jobUuid,
        type: changeOrder.notes || "change_order",
        description: changeOrder.summary,
        amount_delta: changeOrder.changeAmount,
        status: changeOrder.status,
        requested_by: userId,
        approved_by: changeOrder.status === "Signed" ? userId : null,
      })
      .select()
      .single();

    if (orderError || !orderRow) {
      console.warn("[JobOperationsService.createChangeOrder] order error:", orderError);
      return null;
    }

    AuditLogService.create({
      actionType: "change_order_created",
      entityType: "change_order",
      entityId: (orderRow as ChangeOrderRow).id,
      outcome: "success",
      metadata: { amountDelta: changeOrder.changeAmount, type: changeOrder.notes, description: changeOrder.summary, requestedBy: userId },
    }).catch(() => {});

    if (changeOrder.lineItems.length > 0) {
       const { error: itemError } = await supabase
         .from("change_order_items")
         .insert(
           changeOrder.lineItems.map((item) => ({
             change_order_id: (orderRow as ChangeOrderRow).id,
             description: item.description,
             quantity: item.quantity ?? 1,
             unit_price: item.amount,
           })),
         );

      if (itemError) {
        console.warn("[JobOperationsService.createChangeOrder] item error:", itemError);
      }
    }

    const latest = await this.listChangeOrdersByJob(jobNumber);
    const saved = latest.find((row) => row.id === (orderRow as ChangeOrderRow).id);
    if (!saved) return null;

    return {
      ...saved,
      originalContractValue: changeOrder.originalContractValue,
      revisedContractValue: changeOrder.revisedContractValue,
      homeownerSignature: changeOrder.homeownerSignature,
      homeownerSignedAt: changeOrder.homeownerSignedAt,
      repSignature: changeOrder.repSignature,
      repSignedAt: changeOrder.repSignedAt,
      notes: changeOrder.notes,
    };
  },

  async updateChangeOrderStatus(id: string, status: ChangeOrder["status"]): Promise<boolean> {
    const userId = await getCurrentUserId();
    const { error } = await supabase
      .from("change_orders")
      .update({ status })
      .eq("id", id);

    if (error) {
      console.warn("[JobOperationsService.updateChangeOrderStatus] error:", error);
      return false;
    }

    if (status === "Signed") {
      AuditLogService.create({
        actionType: "change_order_signed",
        entityType: "change_order",
        entityId: id,
        outcome: "success",
        metadata: { signedBy: userId, signedAt: new Date().toISOString() },
      }).catch(() => {});
    }

    return true;
  },

  async deleteChangeOrder(id: string): Promise<boolean> {
    const { error: itemError } = await supabase
      .from("change_order_items")
      .delete()
      .eq("change_order_id", id);

    if (itemError) {
      console.warn("[JobOperationsService.deleteChangeOrder] item error:", itemError);
      return false;
    }

    const { error } = await supabase
      .from("change_orders")
      .delete()
      .eq("id", id);

    if (error) {
      console.warn("[JobOperationsService.deleteChangeOrder] order error:", error);
      return false;
    }

    return true;
  },

  async listInstallerWrapRecords(jobLookup?: JobLookup[]): Promise<InstallerWrapRecord[]> {
    const { data, error } = await supabase
      .from("installer_wrap_records")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) {
      console.warn("[JobOperationsService.listInstallerWrapRecords] error:", error);
      return [];
    }

    const lookup = jobLookup ?? await this.getJobLookup();
    const jobNumberByUuid = new Map<string, string>(
      lookup.map((row): [string, string] => [row.id, row.job_number ?? row.id]),
    );

    return (data as InstallerWrapRecordRow[])
      .map((row) => {
        const jobNumber = jobNumberByUuid.get(row.job_id);
        return jobNumber ? toUiWrapRecord(row, jobNumber) : null;
      })
      .filter((row): row is InstallerWrapRecord => Boolean(row));
  },

  async createInstallerWrapRecord(jobNumber: string, record: InstallerWrapRecord): Promise<InstallerWrapRecord | null> {
    const [jobUuid, organizationId, userId] = await Promise.all([
      resolveJobUuid(jobNumber),
      getCurrentOrganizationId().catch(() => null),
      getCurrentUserId(),
    ]);

    if (!jobUuid || !organizationId) return null;

    const checklist = {
      timestamp: record.timestamp,
      progressNotes: record.progressNotes,
      anotherDayNeeded: record.anotherDayNeeded,
      customerComm: record.customerComm,
      settlementStatus: record.settlementStatus,
      schedulingFollowUpRequired: record.schedulingFollowUpRequired,
      closeoutEligible: record.closeoutEligible,
      completionPercent: record.completionPercent,
      paymentCapture: record.paymentCapture,
      serviceChecklist: record.serviceChecklist,
    };

    const { data, error } = await supabase
      .from("installer_wrap_records")
      .insert({
        organization_id: organizationId,
        job_id: jobUuid,
        installer_id: userId,
        wrap_date: record.timestamp.slice(0, 10),
        status: record.closeoutEligible ? "completed" : "submitted",
        outcome: record.outcome,
        checklist,
        notes: record.progressNotes,
      })
      .select()
      .single();

    if (error || !data) {
      console.warn("[JobOperationsService.createInstallerWrapRecord] error:", error);
      return null;
    }

    AuditLogService.create({
      actionType: "wrap_record_created",
      entityType: "job",
      entityId: jobNumber,
      outcome: "success",
      metadata: { outcome: record.outcome, completionPercent: record.completionPercent },
    }).catch(() => {});

    return toUiWrapRecord(data as InstallerWrapRecordRow, jobNumber);
  },

  async listInstallFollowUpTasks(jobLookup?: JobLookup[]): Promise<InstallFollowUpTask[]> {
    const { data, error } = await supabase
      .from("install_follow_up_tasks")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) {
      console.warn("[JobOperationsService.listInstallFollowUpTasks] error:", error);
      return [];
    }

    const lookup = jobLookup ?? await this.getJobLookup();
    const jobNumberByUuid = new Map<string, string>(
      lookup.map((row): [string, string] => [row.id, row.job_number ?? row.id]),
    );

    return (data as InstallFollowUpTaskRow[])
      .map((row) => {
        const jobNumber = jobNumberByUuid.get(row.job_id);
        return jobNumber ? toUiInstallFollowUpTask(row, jobNumber) : null;
      })
      .filter((row): row is InstallFollowUpTask => Boolean(row));
  },

  async createInstallFollowUpTask(jobNumber: string, task: InstallFollowUpTask): Promise<InstallFollowUpTask | null> {
    const [jobUuid, organizationId, userId] = await Promise.all([
      resolveJobUuid(jobNumber),
      getCurrentOrganizationId().catch(() => null),
      getCurrentUserId(),
    ]);

    if (!jobUuid || !organizationId) return null;

    const { data, error } = await supabase
      .from("install_follow_up_tasks")
      .insert({
        organization_id: organizationId,
        job_id: jobUuid,
        wrap_record_id: task.wrapRecordId || null,
        task_type: task.reason,
        status: task.status,
        due_date: task.proposedNextDate ? new Date(task.proposedNextDate).toISOString() : null,
        assigned_to: userId,
        notes: JSON.stringify({
          reasonLabel: task.reasonLabel,
          communicationSummary: task.communicationSummary,
          proposedNextDate: task.proposedNextDate,
          completionPercent: task.completionPercent,
        }),
      })
      .select()
      .single();

    if (error || !data) {
      console.warn("[JobOperationsService.createInstallFollowUpTask] error:", error);
      return null;
    }

    return toUiInstallFollowUpTask(data as InstallFollowUpTaskRow, jobNumber);
  },

  async completeInstallFollowUpTask(id: string, notes?: string): Promise<boolean> {
    const { error } = await supabase
      .from("install_follow_up_tasks")
      .update({
        status: "completed",
        notes: notes ?? null,
      })
      .eq("id", id);

    if (error) {
      console.warn("[JobOperationsService.completeInstallFollowUpTask] error:", error);
      return false;
    }

    return true;
  },

  getContractorAvailability(
    contractorId: string,
    contractors: Contractor[],
    events: SchedulingEventRow[],
    startAt: string,
    endAt: string,
    excludeEventId?: string,
  ) {
    const contractor = contractors.find((item) => item.id === contractorId);
    const overlappingEvents = events.filter((event) => {
      if (event.id === excludeEventId) return false;
      if (event.contractor_id !== contractorId) return false;
      if (!event.end_at) return false;

      const eventStart = new Date(event.start_at).getTime();
      const eventEnd = new Date(event.end_at).getTime();
      const nextStart = new Date(startAt).getTime();
      const nextEnd = new Date(endAt).getTime();
      return eventStart < nextEnd && nextStart < eventEnd;
    });

    return {
      contractor,
      available: contractor?.availabilityStatus !== "Unavailable" && overlappingEvents.length === 0,
      overlappingEvents,
    };
  },
};
